/**
 * PixVerse descriptors (Phase 1, unit U5): pixverse-5.5, pixverse-6.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * Reproduces today's behaviour (U0 goldens, families/pixverse.parity.test.ts):
 *   editor   server/routes/falai.ts case 'pixverse-5.5'/'pixverse-6' :1691-1725
 *   headless server/services/ProviderService.ts generic shape :1333-1346 + branch :1514-1526
 *   price    server/services/CreditService.ts generic tail :1468-1475 (row × ceil(raw seconds) × ladder(raw resolution))
 *
 * Schemas: __schemas__/fal-ai__pixverse__v5.5__*.json, fal-ai__pixverse__v6__*.json (oracle 2026-09-19):
 * v5.5 duration is a STRING enum "5"|"8"|"10", v6 an INTEGER 1..15; resolution 360p..1080p;
 * aspect_ratio on text-to-video only; audio is `generate_audio_switch`.
 */
import type { ModelDescriptor, Param } from '../types';
import { IMG, W_IMG } from '../_shared';

const SCHEMA_DATE = '2026-09-19';

/**
 * Resolution: priced by the generic LADDER over the raw value (so '4k' bills 3x, '480p' 0.75x),
 * rendered at `resolution === '1080p' ? '1080p' : '720p'` (editor) / `String(r || '720p') === '1080p'`
 * (headless) — the same rule. Kept raw (passthrough) because the ladder reads the raw spelling.
 */
const resolution: Param = {
  id: 'resolution', type: 'enum', values: ['720p', '1080p'], uiValues: ['720p', '1080p'], default: '720p', role: 'tier',
  invalid: 'passthrough',
  wire: { field: 'resolution', map: { '1080p': '1080p' }, mapFallback: '720p' },
  ui: { label: 'Resolution', tier: 'basic' },
};

/**
 * Aspect ratio (text-to-video only). Editor: `16:9 | 9:16 | else 1:1` over the SANITIZED value
 * (an unknown ratio was already turned into 16:9 by sanitizeAspectRatio). Headless: the raw value
 * (`aspectRatio || '16:9'`), unknown strings included — reproduced by the headless legacy.wire.
 */
const aspectRatio: Param = {
  id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16', '1:1'], default: '16:9', onlyModes: ['t2v'],
  // `if (isTextToVideo)` in the editor arm ($t2v: false on the route's t2v-endpoint fallbacks).
  sendWhen: { $t2v: { eq: true } },
  invalid: 'passthrough',
  wire: { field: 'aspect_ratio', map: { '16:9': '16:9', '9:16': '9:16' }, mapFallback: '1:1' },
  ui: { label: 'Aspect ratio', tier: 'basic' },
};

/** Legacy `if (enableAudio && supportsAudio)` is JS truthiness. The node has no audio toggle. */
const enableAudio: Param = {
  id: 'enableAudio', type: 'bool', default: false, aliases: { true: true, '1': true, yes: true },
  invalid: { coerce: { kind: 'default' } }, wire: false,
  ui: { label: 'Generate audio', tier: 'hidden' },
};

const legacyWire = [
  { field: 'aspect_ratio', fromParam: 'aspectRatio', modes: ['t2v'], surface: 'headless',
    why: "headless sends the raw ratio (ProviderService.ts:1336, `aspectRatio || '16:9'`) where the editor maps anything else to 1:1",
    retire: 'owner-approval' },
  { field: 'generate_audio_switch', fromParam: 'enableAudio', map: { true: true }, surface: 'editor',
    why: 'editor sends generate_audio_switch when enableAudio is truthy (falai.ts:1722) and bills no audio surcharge; headless never sends it',
    retire: 'owner-approval' },
] as const;

const omits = [{ field: 'generate_audio_switch', why: 'audio off -> omitted; fal default false (no audio), which is what is billed' }];

const pixverse = (v: '5.5' | '6'): ModelDescriptor => {
  const id = `pixverse-${v}`;
  const slug = (m: string) => `fal-ai/pixverse/v${v}/${m}`;
  const duration: Param = v === '5.5'
    // round(Number(d) || 5) then >= 10 -> "10", >= 8 -> "8", else "5". Seconds clamp to 5..10 and
    // the wire map floors them onto the enum (6 s renders 5 s; 9 s renders 8 s) — billed RAW.
    ? { id: 'duration', type: 'int', min: 5, max: 10, uiValues: [5, 8], default: 5, role: 'units', normalize: 'trim',
        aliases: { '0': 5 },
        invalid: { coerce: { kind: 'clamp', min: 5, max: 10 } },
        wire: { field: 'duration', as: 'string', map: { 5: '5', 6: '5', 7: '5', 8: '8', 9: '8', 10: '10' } },
        ui: { label: 'Duration', tier: 'basic' } }
    // min(15, max(1, round(Number(d) || 5))), billed RAW. `|| 5`: a zero becomes 5, not 1.
    : { id: 'duration', type: 'int', min: 1, max: 15, uiValues: [5, 8], default: 5, role: 'units', normalize: 'trim',
        aliases: { '0': 5 },
        invalid: { coerce: { kind: 'clamp', min: 1, max: 15 } },
        wire: { field: 'duration', as: 'integer' },
        ui: { label: 'Duration', tier: 'basic' } };
  return {
    id, provider: 'falai', face: 'video', displayName: v === '6' ? 'PixVerse v6' : 'PixVerse v5.5',
    ledgerName: `PixVerse ${v}`, status: 'hidden',
    prompt: { field: 'prompt', required: true },
    modes: {
      t2v: { endpoint: slug('text-to-video'), ports: [], media: {} },
      i2v: { endpoint: slug('image-to-video'), ports: [IMG()], media: { image: W_IMG } },
    },
    params: [duration, resolution, aspectRatio, enableAudio],
    legacy: { wire: legacyWire, omits },
    pricing: {
      // v6: 'resolved' (P1-WMLP) — the clamp coercion is pixverseSeconds('6') (round, 1..15), so
      // the engine bills the length it wires, as d01af92 legacy does. v5.5 stays 'raw': its wire
      // map FLOORS onto 5/8/10 (6 s renders 5 s) and no coercion expresses a floor-snap, so its
      // resolved value (6) is not the rendered one either. The legacy mappers hand the engine
      // pixverseSeconds() already (a fixed point of the map), so replay bills 5 there.
      unit: 'second', units: { param: 'duration', stage: v === '6' ? 'resolved' : 'raw' }, rounding: 'generic', policy: 'fal-video-35',
      skus: [v === '6'
        ? { id, rate: { ladder: { base: 21, by: 'resolution', curve: 'legacy-resolution-v1' } },
            vendor: { usd: { '720p': 0.045, '1080p': 0.09 }, per: 'second', field: 'pricingInfoOverride',
                      text: 'For 720p, it will cost **$0.045** per second without audio and **$0.060** per second with audio. For 1080p, it will cost **$0.090** per second without audio and **$0.115** per second with audio.',
                      source: 'fal-ai/pixverse/v6/text-to-video', verified: '2026-09-19' },
            // REVIEW ROUND 1, defect 5: the 720p base IS the policy rate (ceil(0.045 × 300 / 0.65) = 21),
            // but the row is priced through the generic ladder, so 1080p bills 21 × 1.5 = 31.5 cr/s
            // against a $0.09/s vendor price whose policy rate is 42 — a ~25% margin shortfall on the
            // 1080p tier. P1 is price-neutral, so the live rate is UNCHANGED and the gap is declared
            // here instead of silently passing a base-tier-only policy check (the family test now
            // evaluates every priced ladder tier). Listed in decisionsForOwner for a P2 reprice.
            marginException: 'ladder tier off policy: 1080p bills base × 1.5 = 31.5 cr/s where fal charges $0.09/s (policy 42). The 720p base (21) is on policy. Unchanged in P1 (price-neutral).',
            registry: { displayName: 'PixVerse v6', operationType: 'video', publishedTier: '720p', flatRate: false } }
        : { id, rate: { ladder: { base: 18, by: 'resolution', curve: 'legacy-resolution-v1' } },
            vendor: { usd: { '720p': 0.04, '1080p': 0.08 }, per: 'second', field: 'pricingInfoOverride',
                      text: 'For a 5s video in single-clip mode without audio, your request will cost $0.15 for 360p and 540p, $0.2 for 720p, and $0.4 for 1080p. Enabling audio adds $0.05, and multi-clip mode adds $0.10 (or $0.15 with audio). For 8-second videos, costs double; for 10-second videos, costs are 2.2x the 5-second base (1080p not supported for 10s).',
                      source: 'fal-ai/pixverse/v5.5/text-to-video (per-clip prices ÷ 5 s)', verified: '2026-09-19' },
            marginException: 'fal prices v5.5 per CLIP ($0.20 per 5 s at 720p = $0.04/s -> policy 19; 8 s costs 2x = $0.05/s; 10 s 2.2x), the live row bills 18 cr/s flat per second. Unchanged in P1 (price-neutral).',
            registry: { displayName: 'PixVerse 5.5', operationType: 'video', publishedTier: '720p', flatRate: false } }],
    },
    knowledge: { summary: v === '6' ? 'PixVerse v6: 1-15 s, 720p/1080p.' : 'PixVerse v5.5: 5/8/10 s clips, 720p/1080p (no 1080p at 10 s).' },
    schemaVerified: SCHEMA_DATE,
  };
};

export const PIXVERSE_55 = pixverse('5.5');
export const PIXVERSE_6 = pixverse('6');

export const PIXVERSE_MODELS: readonly ModelDescriptor[] = [PIXVERSE_6, PIXVERSE_55];
